/** Endpoints du panneau d'administration — /api/v1/admin (blocs 0-1). */

import { apiFetch } from "@/api/client";

export type AdminOverview = {
  total_users: number;
  premium_users: number;
  pending_deletions: number;
  total_logements: number;
  dpe_calcule: number;
};

export type AdminUserListItem = {
  id: string;
  email: string;
  tier: "free" | "premium";
  is_admin: boolean;
  email_verified: boolean;
  created_at: string;
  last_login_at: string | null;
  deletion_requested_at: string | null;
  logements_count: number;
};

export type AdminUserList = {
  items: AdminUserListItem[];
  total: number;
  limit: number;
  offset: number;
};

export type AdminHealth = {
  db_size_bytes: number;
  db_size_pretty: string;
  users: number;
  logements: number;
  dpe_total: number;
  dpe_calcule: number;
  dpe_brouillon: number;
  pending_deletions: number;
};

export function getAdminOverview() {
  return apiFetch<AdminOverview>("/admin/overview");
}

export function listAdminUsers(params: { q?: string; limit?: number; offset?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<AdminUserList>(`/admin/users${suffix}`);
}

export function setAdminUserTier(userId: string, tier: "free" | "premium") {
  return apiFetch<AdminUserListItem>(`/admin/users/${userId}/set-tier`, {
    method: "POST",
    body: { tier },
  });
}

export function verifyAdminUserEmail(userId: string) {
  return apiFetch<AdminUserListItem>(`/admin/users/${userId}/verify-email`, {
    method: "POST",
  });
}

export function requestAdminUserDeletion(userId: string) {
  return apiFetch<AdminUserListItem>(`/admin/users/${userId}/request-deletion`, {
    method: "POST",
  });
}

/** URL d'export RGPD (ouverte dans un nouvel onglet — cookie de session inclus). */
export function adminUserExportUrl(userId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  return `${base}/admin/users/${userId}/export`;
}

export function getAdminHealth() {
  return apiFetch<AdminHealth>("/admin/health");
}

// --- Bloc 2 : métriques moteur, paramètres (lecture seule), feature flags ---

export type LabelCount = { label: string | null; count: number };

export type AdminEngineMetrics = {
  dpe_calcule: number;
  dpe_brouillon: number;
  energy_labels: LabelCount[];
  ges_labels: LabelCount[];
  engine_versions: LabelCount[];
};

export type AdminConfig = {
  energy_unit_prices: Record<string, unknown>;
  prices: Record<string, unknown>;
};

export type FeatureToggle = {
  key: string;
  enabled: boolean;
  description: string | null;
};

export function getAdminEngineMetrics() {
  return apiFetch<AdminEngineMetrics>("/admin/engine-metrics");
}

export function getAdminConfig() {
  return apiFetch<AdminConfig>("/admin/config");
}

export function listFeatureFlags() {
  return apiFetch<FeatureToggle[]>("/admin/feature-flags");
}

export function setFeatureFlag(key: string, enabled: boolean, description?: string | null) {
  return apiFetch<FeatureToggle>("/admin/feature-flags", {
    method: "POST",
    body: { key, enabled, description: description ?? null },
  });
}

// --- Liens partenaires (affiliation) : activation section par section ---

export type AdminAffiliation = {
  key: string;
  label: string;
  is_official: boolean;
  enabled: boolean;
};

export function getAdminAffiliations() {
  return apiFetch<AdminAffiliation[]>("/admin/affiliations");
}

export function setAdminAffiliation(key: string, enabled: boolean) {
  return apiFetch<AdminAffiliation>(`/admin/affiliations/${key}`, {
    method: "POST",
    body: { enabled },
  });
}

// --- Seuils de rentabilité (ROI) par geste ---

export type RoiThresholdItem = {
  key: string;
  label: string;
  category: string;
  years: number;
  configured: boolean;
};

export type RoiThresholds = {
  default_years: number;
  items: RoiThresholdItem[];
};

export function getRoiThresholds() {
  return apiFetch<RoiThresholds>("/admin/roi-thresholds");
}

export function setRoiThreshold(key: string, years: number) {
  return apiFetch<RoiThresholds>(`/admin/roi-thresholds/${key}`, {
    method: "POST",
    body: { years },
  });
}

// --- Prix de l'énergie (éditables) ---

export type EditableEnergy = {
  key: string;
  label: string;
  abonnement_annuel_eur: number;
  eur_per_kwh?: number | null;
  eur_per_kwh_hc?: number | null;
  eur_per_kwh_hp?: number | null;
  abonnement_par_kva?: Record<string, number> | null;
};

export type EnergyPrices = {
  reference_year: number | null;
  range_tolerance_pct: number | null;
  last_updated: string | null;
  is_override: boolean;
  energies: EditableEnergy[];
};

export type SetEnergyPricesBody = {
  reference_year: number;
  range_tolerance_pct: number;
  last_updated?: string | null;
  energies: Omit<EditableEnergy, "label">[];
};

export function getEnergyPrices() {
  return apiFetch<EnergyPrices>("/admin/energy-prices");
}

export function setEnergyPrices(body: SetEnergyPricesBody) {
  return apiFetch<EnergyPrices>("/admin/energy-prices", {
    method: "POST",
    body,
  });
}

export function resetEnergyPrices() {
  return apiFetch<EnergyPrices>("/admin/energy-prices", { method: "DELETE" });
}

// --- Modèle de page d'accueil ---

export type HomeVariantItem = { id: string; label: string; description: string };
export type HomeVariantState = { current: string; variants: HomeVariantItem[] };

export function getAdminHomeVariant() {
  return apiFetch<HomeVariantState>("/admin/home-variant");
}

export function setAdminHomeVariant(id: string) {
  return apiFetch<HomeVariantState>("/admin/home-variant", {
    method: "POST",
    body: { id },
  });
}

// --- Modèle du panneau simulation travaux (V1 bouquets ↔ V2 interactive) ---

export type ScenariosPanelVariantItem = {
  id: string;
  label: string;
  description: string;
};
export type ScenariosPanelVariantState = {
  current: string;
  variants: ScenariosPanelVariantItem[];
};

export function getAdminScenariosPanelVariant() {
  return apiFetch<ScenariosPanelVariantState>("/admin/scenarios-panel-variant");
}

export function setAdminScenariosPanelVariant(id: string) {
  return apiFetch<ScenariosPanelVariantState>("/admin/scenarios-panel-variant", {
    method: "POST",
    body: { id },
  });
}

// --- Objectifs de rénovation (classement des gestes par objectif) ---

export function listRenovationObjectives() {
  return apiFetch<unknown>("/admin/renovation-objectives");
}

export function getObjective(objectiveKey: string) {
  return apiFetch<unknown>(`/admin/renovation-objectives/${objectiveKey}`);
}

export function rankObjectiveForLogement(
  objectiveKey: string,
  payload: {
    logement_id: string;
    dpe_id?: string;
    household?: {
      tax_reference_income_eur?: number;
      household_size?: number;
      department_code?: string;
    };
  },
) {
  return apiFetch<unknown>(`/admin/renovation-objectives/${objectiveKey}/rank`, {
    method: "POST",
    body: payload,
  });
}
